/* eslint-disable max-classes-per-file */
import {
    useEffect, useMemo, useRef, useState,
} from "react";
import {
    Chat,
    GetChatDocument,
    GetChatQuery,
    GetChatQueryVariables,
    GetTicketDocument,
    GetTicketQuery,
    GetTicketQueryVariables,
    Permission,
    useListAgentChatQuery,
} from "../../__generated__/api";
import TicketItem from "./ticket-item";
import SelectionState from "./selection";
import UserCache from "./user-cache";
import { LoggingHandler } from "../logging/LoggingContext";
import client from "../../api";
import ColumnStateCache from "./column-state-cache";

type TicketsQuery = ReturnType<typeof useListAgentChatQuery>;

export default class TicketState {
    public static useTicketState(
        chatsQuery: TicketsQuery,
        state: SelectionState,
        userCache: UserCache,
    ) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const ticketState = useMemo(() => new TicketState(state, userCache, chatsQuery), [state, userCache]);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useMemo(() => {
            chatsQuery.data?.chats?.chats.dedupe(row => row.id).forEach(chat => {
                const result = ticketState.get(chat.id);
                if (!result) {
                    ticketState.add(chat as Chat);
                }
            });
        }, [chatsQuery.data]);

        return ticketState;
    }

    private ticketMap: Record<string, TicketItem> = Object.create(null);

    private ticketNumberMap: Record<string, TicketItem> = Object.create(null);

    private ticketSearchMap: Record<string, TicketItem> = Object.create(null);

    private _columnStateCache?: ColumnStateCache;

    // eslint-disable-next-line no-useless-constructor
    private constructor(
        private state: SelectionState,
        private userCache: UserCache,
        private chatsData: TicketsQuery,
    ) {
        // none
    }

    public setSearch(tickets: TicketItem[] | undefined) {
        this.ticketSearchMap = Object.create(null);
        tickets?.forEach(ticket => {
            this.ticketSearchMap[ticket.id] = ticket;
            if (ticket.ticket.ticketNumber) {
                this.ticketSearchMap[ticket.ticket.ticketNumber] = ticket;
            }
        });
    }

    public useGetOrFetch(id: string) {
        const isNew = id === "new";
        const cachedTicket = !isNew ? (this.get(id) ?? this.getSearch(id)) : undefined;

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [, setUpdate] = useState<unknown>();
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const loadingRef = useRef(false);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [error, setError] = useState<string>();

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            const cached = this.get(id);
            if (id && !cached) {
                loadingRef.current = false;
                setUpdate({});
            }
            setError(undefined);
        }, [id]);

        if (isNew) {
            return {
                isNew,
            };
        }

        if (cachedTicket || error) {
            return {
                ticket: cachedTicket,
                isLoading: false,
                error,
            };
        }

        if (!loadingRef.current) {
            LoggingHandler.instance.nextTick(async () => {
                if (loadingRef.current || !id) {
                    return;
                }
                loadingRef.current = true;
                try {
                    await this.fetch(id);
                } catch (err) {
                    setError("Error loading ticket");
                }

                setUpdate({});
            });
        }

        return {
            isLoading: true,
            error,
        };
    }

    public async fetch(id: string) {
        const search = this.getSearch(id);

        if (search) {
            return search;
        }

        if (this.userCache.currentPermissions?.has(Permission.ChatsRead)) {
            const chat = await client.query<GetChatQuery, GetChatQueryVariables>({
                query: GetChatDocument,
                variables: {
                    id,
                },
            });
            return this.add(chat.data.chat as Chat);
        }

        const ticket = await client.query<GetTicketQuery, GetTicketQueryVariables>({
            query: GetTicketDocument,
            variables: {
                id,
            },
        });
        return this.add({
            ...ticket.data.ticket,
            ticket: ticket.data.ticket,
        } as unknown as Chat);
    }

    public async refresh() {
        LoggingHandler.instance.nextTick(async () => {
            const refetched = await this.chatsData?.refetch();
            if (refetched?.data?.chats?.chats) {
                refetched.data.chats.chats.forEach(chat => {
                    const result = this.get(chat.id);
                    if (result) {
                        result.resync(chat as Chat);
                    } else {
                        this.add(chat as Chat);
                    }
                });
            }
        });
    }

    public setColumnStateCache(cache: ColumnStateCache) {
        this._columnStateCache = cache;
    }

    public get columnStateCache() {
        return this._columnStateCache;
    }

    public async getOrFetch(id: string) {
        return this.get(id) ?? this.fetch(id);
    }

    public getTickets() {
        return Object.values(this.ticketMap);
    }

    public get(id: string) {
        return this.ticketMap[id] ?? this.ticketNumberMap[id];
    }

    public delete(id: string) {
        delete this.ticketMap[id];
        delete this.ticketNumberMap[id];
    }

    public getSearch(id: string) {
        return this.ticketSearchMap[id];
    }

    public create(chat: Chat) {
        return new TicketItem(chat, this.state, this.userCache, this);
    }

    public add(chat: Chat) {
        const item = this.create(chat);
        this.ticketMap[chat.id] = item;
        if (chat.ticketNumber) {
            this.ticketNumberMap[chat.ticketNumber] = item;
        }
        return item;
    }
}
